import { useMatches } from 'react-router-dom';
import { useSessionContext } from '@/contexts/session/context';
import type { ActionKeys } from '@/configs/router/actions';
import { routeMap } from '@/configs/router/routeMap';
import type Paths from '../../configs/router/paths';

export interface Auth {
  key: string;
  actions: ActionKeys[];
}

export function useAccess() {
  const {
    session: { user },
  } = useSessionContext();

  return useCallback(
    // Page access
    (key: string | undefined) => {
      let pageAccess: boolean | string[] = true;

      if (key) {
        if (user) {
          const { access } = user;
          if (access !== '*') {
            if (access[key]) {
              pageAccess = access[key];
            } else {
              pageAccess = false;
            }
          }
        } else {
          pageAccess = true;
        }
      }
      // Action access
      return (action: string) => {
        if (pageAccess) {
          if (Array.isArray(pageAccess)) {
            return pageAccess.includes(action);
          }
          return true;
        }
        return false;
      };
    },
    [user]
  );
}

export function getAuth(path: Paths) {
  return routeMap[path]?.auth;
}

export function getAuthKey(path: Paths) {
  return getAuth(path)!.key;
}

export function useRouteAuth() {
  const matches = useMatches();

  const auth = useMemo(() => {
    let auth: Auth | undefined;
    for (let i = matches.length - 1; i > -1; i--) {
      const route = matches[i];
      if ((auth = getAuth(route.pathname as Paths))) {
        break;
      }
    }
    return auth;
  }, [matches]);

  return auth;
}

export function useRouteAccess() {
  const auth = useRouteAuth();
  return useAccess()(auth?.key);
}

export function usePathAccess(path: Paths) {
  return useAccess()(getAuthKey(path));
}
